import { queryOptions } from "@tanstack/react-query";
import {
    getOcrResponse,
    InvoiceTableDataDto,
    InvoiceQueryFilter,
    createAttachment,
    createBankAccount,
    deleteAttachment,
    deleteInvoice,
    getInvoiceSender,
    getInvoice,
    getInvoices,
    submitInvoice,
    updateInvoice,
    InvoiceSenderDto,
    InvoiceFromOcrDto,
    createEmptyInvoice,
    RejectedInvoiceTableDataDto,
    getRejectedInvoices,
    ocrScanInvoiceAttachment,
    RejectedQueryFilter
} from "../invoiceApi";
import { NormalizedSearchResultDto } from "../claimsInvoicingApi";
import { InsertInvoiceDto, BankAccountDto, InvoiceDetailsDto } from "@/types";

type UpdateInvoiceByPutMutation = {
    id: string;
    invoice: InsertInvoiceDto;
};

type PostBankAccountMutation = {
    id: string;
    bankAccount: BankAccountDto;
};

type UploadAttachmentByPostMutation = {
    id: string;
    form: FormData;
};

type DeleteAttachmentMutation = {
    id: string;
};

type SubmitInvoiceByPostMutation = {
    id: string;
    invoiceDetails: InsertInvoiceDto;
};

const getInvoicesQueryOptions = (queryFilter: InvoiceQueryFilter) =>
    queryOptions<NormalizedSearchResultDto<InvoiceTableDataDto>>({
        queryKey: ["invoices", queryFilter],
        queryFn: () => getInvoices(queryFilter)
    });

const getRejectedInvoicesQueryOptions = (queryFilter: RejectedQueryFilter) =>
    queryOptions<NormalizedSearchResultDto<RejectedInvoiceTableDataDto>>({
        queryKey: ["rejectedInvoices", queryFilter],
        queryFn: () => getRejectedInvoices(queryFilter)
    });

const getInvoiceQueryOptions = (invoiceId: string) =>
    queryOptions<InvoiceDetailsDto>({
        queryKey: ["invoice", invoiceId],
        queryFn: () => getInvoice(invoiceId),
        enabled: Boolean(invoiceId)
    });

const getInvoiceSenderQueryOptions = (crmId: string, invoiceId?: string) =>
    queryOptions<InvoiceSenderDto>({
        queryKey: ["invoiceSender", crmId, invoiceId],
        queryFn: () => getInvoiceSender(crmId, invoiceId),
        enabled: Boolean(crmId) && Boolean(invoiceId)
    });

const getOcrResponseQueryOptions = (invoiceId: string, docId: string, enabled = true) =>
    queryOptions<InvoiceFromOcrDto>({
        queryKey: ["ocrResponse", docId],
        queryFn: () => getOcrResponse(docId),
        enabled: Boolean(invoiceId) && Boolean(docId) && enabled,
        retryOnMount: false
    });

const ocrScanInvoiceAttachmentMutationOptions = {
    mutationFn: (docId: string) => ocrScanInvoiceAttachment(docId)
};

const createEmptyInvoiceMutationOptions = {
    mutationFn: ({ crmId }: { crmId?: string }) => createEmptyInvoice(crmId)
};

const updateInvoiceByPutMutationOptions = {
    mutationFn: ({ id, invoice }: UpdateInvoiceByPutMutation): Promise<string> => updateInvoice(id, invoice)
};

const deleteInvoiceMutationOptions = {
    mutationFn: (id: string) => deleteInvoice(id)
};

const postBankAccountMutationOptions = {
    mutationFn: ({ id, bankAccount }: PostBankAccountMutation) => createBankAccount(id, bankAccount)
};

const uploadAttachmentByPostMutationOptions = {
    mutationFn: ({ id, form }: UploadAttachmentByPostMutation) => createAttachment(id, form)
};

const deleteAttachmentMutationOptions = {
    mutationFn: ({ id }: DeleteAttachmentMutation) => deleteAttachment(id)
};

const submitInvoiceByPostMutationOptions = {
    mutationFn: ({ id, invoiceDetails }: SubmitInvoiceByPostMutation) => submitInvoice(id, invoiceDetails)
};

export {
    getInvoicesQueryOptions,
    getRejectedInvoicesQueryOptions,
    getInvoiceQueryOptions,
    getInvoiceSenderQueryOptions,
    getOcrResponseQueryOptions,
    ocrScanInvoiceAttachmentMutationOptions,
    createEmptyInvoiceMutationOptions,
    updateInvoiceByPutMutationOptions,
    deleteInvoiceMutationOptions,
    postBankAccountMutationOptions,
    uploadAttachmentByPostMutationOptions,
    deleteAttachmentMutationOptions,
    submitInvoiceByPostMutationOptions
};
